import { useSoftphone } from "@dialstack/sdk-react/softphone";
import { ReactNode, useEffect } from "react";

import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { neutral } from "app/themes/colors";
import { dialDeviceLabel } from "components/suno-dialer/call-patient/dial-device-copy";
import type { UserDevice } from "services/dialstack";

import { displayCallerNumber } from "../call-display.util";
import { popCallerNumber } from "../incoming-call-pop.slice";
import CallNoteField from "./call-note-field.component";
import {
  DESK_PHONE_HANDSET_HINT,
  useElapsed
} from "./desk-phone-call-card.component";
import {
  WHITE_SOFT,
  callCardSx,
  callCardsSx,
  callPopHeaderSx,
  callPopHeaderTextSx,
  callPopSx
} from "./dialstack-softphone-widget.styles";
import { PopPatients } from "./incoming-call-patient-card.component";
import SoftphoneStatusButton from "./softphone-status-button.component";
import type { DeviceCall } from "./use-device-call.hook";
import { useDialstackSoftphone } from "./use-dialstack-softphone.hook";

// A finished call keeps its card up this long, so a note started during the
// call can be finished; then the dial pad comes back on its own.
export const DEVICE_CALL_ENDED_LINGER_MS = 60_000;
export const CALL_ENDED_TITLE = "Call ended";
// The browser's ringing leg just ended and the backend has not yet said whose
// phone the call is on.
export const CONNECTING_TITLE = "Connecting…";

// "On your cordless phone" when the user's hardware is handsets, "On your
// desk phone" otherwise; with no hardware listed the call went to a phone
// Suno does not know about (the mobile app, a forwarded cell).
export function deviceCallTitle(devices: UserDevice[]) {
  if (devices.length === 0) return "On another phone";
  return `On your ${dialDeviceLabel(devices).toLowerCase()}`;
}

const dismissTextSx = {
  textTransform: "none",
  fontSize: 13,
  color: "#FFFFFF",
  mx: 1,
  mb: 0.5,
  alignSelf: "flex-start"
};

// The patient card kept open for a call the user took on their handset
// (ENG-2562, ENG-2605). The same plum frame as the workbench, but the call is
// not in the browser: the header says which phone it is on with the timer,
// the card and the phone note stay, and there are no call controls at all —
// mute, hold, transfer and hang-up happen on the handset, as the hint says.
// It takes the ringing pop's place the moment the browser's leg ends
// ("Connecting…"), so the card never drops to the dial pad in between. Once
// the backend reports the end it reads "Call ended" with a Dismiss button for
// a minute, then goes.
export default function DeviceCallWorkbench({
  call,
  banner,
  children,
  onDismiss
}: {
  call: DeviceCall;
  // Panel-level notices (the 911 location banner).
  banner?: ReactNode;
  // The unknown-caller card for this call, when it is not a patient's.
  children?: ReactNode;
  onDismiss: () => void;
}) {
  const { connection } = useSoftphone();
  const { devices } = useDialstackSoftphone();
  const elapsed = useElapsed(call.answeredAt, call.endedAt);
  const { callId, pop, phase } = call;

  useEffect(() => {
    if (phase !== "ended") return undefined;
    const timer = window.setTimeout(onDismiss, DEVICE_CALL_ENDED_LINGER_MS);
    return () => window.clearTimeout(timer);
  }, [phase, callId, onDismiss]);

  const number = pop ? displayCallerNumber(popCallerNumber(pop)) : "";
  const hasPatient = (pop?.patientIds.length ?? 0) > 0;
  const title =
    phase === "ended"
      ? CALL_ENDED_TITLE
      : phase === "connecting"
        ? CONNECTING_TITLE
        : deviceCallTitle(devices);

  return (
    <Paper elevation={0} data-testid="device-call-workbench" sx={callPopSx}>
      <Box className="suno-phone-drag-handle" sx={callPopHeaderSx}>
        <SoftphoneStatusButton
          connection={connection}
          hasCallActivity={phase !== "ended"}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            component="span"
            data-testid="device-call-title"
            sx={callPopHeaderTextSx}
          >
            {title}
          </Typography>
          <Typography
            noWrap
            data-testid="device-call-timer"
            sx={{
              fontSize: 12,
              color: "rgba(255, 255, 255, 0.78)",
              fontVariantNumeric: "tabular-nums"
            }}
          >
            {[elapsed, number].filter(Boolean).join(" · ")}
          </Typography>
        </Box>
        <Tooltip title="Close">
          <IconButton
            aria-label="Close"
            className="suno-phone-no-drag"
            size="small"
            onClick={onDismiss}
            sx={{ color: "rgba(255, 255, 255, 0.85)" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      {banner}
      <Box aria-live="polite" sx={callCardsSx}>
        {pop?.transfer && (
          <Typography
            data-testid="incoming-call-transferred-by"
            sx={{ fontSize: 12, color: neutral[800], mx: 1.25 }}
          >
            Transferred by {pop.transfer.byName ?? "a colleague"}
          </Typography>
        )}
        {pop && hasPatient && (
          <Box data-testid="incoming-call-card" sx={callCardSx}>
            <PopPatients
              pop={pop}
              backendCallId={call.backendCallId}
              ringing={false}
              heroActions="grid"
            />
          </Box>
        )}
        {children}
      </Box>
      <CallNoteField
        backendCallId={call.backendCallId}
        hasPatient={hasPatient}
      />
      {phase === "active" && (
        <Typography
          data-testid="device-call-hint"
          sx={{ fontSize: 12, color: WHITE_SOFT, mx: 1.5, mb: 1 }}
        >
          {DESK_PHONE_HANDSET_HINT}
        </Typography>
      )}
      {phase === "ended" && (
        <Button
          className="suno-phone-no-drag"
          size="small"
          onClick={onDismiss}
          sx={dismissTextSx}
        >
          Dismiss
        </Button>
      )}
    </Paper>
  );
}
